/**
 * §P.2 · Quién ha avisado de que no viene.
 *
 * ## Existe porque el administrativo no tenía dónde mirar
 *
 * Las justificaciones se veían **solo dentro del `roster` de cada sesión**, así que
 * saber quién avisó esta semana significaba abrir clase por clase. El endpoint se
 * añadió por eso (🆕 en `endpoints.md` §P.2), y esta pantalla es la mitad que
 * faltaba: se descubrió comprobando ruta por ruta qué consume el panel, no usándolo.
 *
 * ## Dos formas de avisar, y las dos son legítimas
 *
 * `session_id` cuando la familia avisa de **una clase concreta**; `absent_from` y
 * `absent_to` cuando avisa de **un tramo** —un viaje, una lesión—. Conviven porque
 * las familias avisan de las dos maneras, y obligar a partir «del 3 al 17» en siete
 * avisos sería pedirle a un padre que haga el trabajo del sistema.
 *
 * La pantalla las pinta distinto a propósito: un tramo de dos semanas y una falta de
 * un martes no son la misma noticia.
 *
 * ## No se cruzan con la asistencia
 *
 * Y es una decisión del contrato, no una limitación: **una familia puede avisar de
 * una falta que luego no lo fue** —el niño apareció—. Mezclarlas daría un número que
 * no cuadra con la lista del maestro sin que nadie sepa por qué. Aquí se enseña lo
 * que la familia dijo, y la lista de quién vino está en la clase.
 */
import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { fecha, fechaYHora } from '@/shared/ui/formato';
import {
  anterior,
  cursorActual,
  esLaPrimera,
  numeroDePagina,
  siguiente,
  PRIMERA,
  type PilaDeCursores,
} from '@/shared/ui/paginas';
import {
  descripcionDeAusencia,
  esTramo,
  type PaginaDeAusencias,
} from '@/club/calendario/tipos';

const POR_PAGINA = 25;

export function AvisosDeAusencia({
  club,
  alAbrirAlumno,
}: {
  club: string;
  alAbrirAlumno: (alumnoId: string) => void;
}) {
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [cursores, setCursores] = useState<PilaDeCursores>(PRIMERA);
  const cursor = cursorActual(cursores);

  const avisos = useQuery({
    queryKey: ['ausencias', club, desde, hasta, cursor],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        ['absence-notices'].get({
          query: {
            limit: POR_PAGINA,
            ...(cursor ? { cursor } : {}),
            ...(desde === '' ? {} : { from: desde }),
            ...(hasta === '' ? {} : { to: hasta }),
          },
        });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as PaginaDeAusencias;
    },
  });

  /* Cambiar de filtro vuelve a la primera página: la pila de cursores es del filtro
     anterior, y seguir con ella daría la página 3 de otra búsqueda. */
  const filtrar = (cambio: () => void) => {
    cambio();
    setCursores(PRIMERA);
  };

  return (
    <section>
      <h1>Quién ha avisado</h1>

      <p className="tenue">
        Las ausencias que han justificado las familias, sin abrir clase por
        clase. <strong>No son la lista de quién faltó</strong>: alguien puede
        avisar y luego aparecer, y eso lo dice la lista de la clase.
      </p>

      <div className="filtros">
        <label>
          Desde
          <input
            type="date"
            value={desde}
            onChange={(e) => filtrar(() => setDesde(e.target.value))}
          />
        </label>
        <label>
          Hasta
          <input
            type="date"
            value={hasta}
            onChange={(e) => filtrar(() => setHasta(e.target.value))}
          />
        </label>
        {(desde !== '' || hasta !== '') && (
          <button
            type="button"
            className="btn-plano"
            onClick={() =>
              filtrar(() => {
                setDesde('');
                setHasta('');
              })
            }
          >
            Quitar las fechas
          </button>
        )}
      </div>

      {avisos.isPending && (
        <Cargando filas={5} etiqueta="Cargando los avisos" />
      )}

      {avisos.isError && (
        <PanelError
          problema={aProblema(avisos.error)}
          reintentar={() => void avisos.refetch()}
        />
      )}

      {avisos.data?.data.length === 0 && (
        <Vacio
          titulo={
            desde === '' && hasta === ''
              ? 'Nadie ha avisado todavía'
              : 'Nadie avisó en esas fechas'
          }
          explicacion={
            desde === '' && hasta === ''
              ? 'Cuando una familia avise desde su app de que su hijo no va a ir —un día o una temporada—, aparecerá aquí.'
              : 'Prueba a ensanchar el rango de fechas.'
          }
        />
      )}

      {avisos.data && avisos.data.data.length > 0 && (
        <>
          <ul className="ausencias">
            {avisos.data.data.map((a) => (
              <li
                key={a.id}
                className={esTramo(a) ? 'fila-marcada' : undefined}
              >
                <div className="aviso-cabeza">
                  <button
                    type="button"
                    className="enlace-fila"
                    onClick={() => alAbrirAlumno(a.student_id)}
                  >
                    <strong>{a.student_name}</strong>
                  </button>
                  {/*
                    El tramo se marca: «del 3 al 17» no es la misma noticia que «el
                    martes no viene», y quien mira esta lista para cuadrar la semana
                    necesita distinguirlas de un vistazo.
                  */}
                  {esTramo(a) && (
                    <span className="pastilla pastilla-info">Varios días</span>
                  )}
                  {a.group_name && (
                    <span className="pastilla pastilla-left">
                      {a.group_name}
                    </span>
                  )}
                </div>

                <p>{descripcionDeAusencia(a)}</p>

                {/*
                  El motivo tal cual lo escribió la familia, y solo si lo escribió:
                  «Motivo: —» ocupa una línea para no decir nada.
                */}
                {a.reason && <p className="tenue">«{a.reason}»</p>}

                <div className="aviso-pie">
                  <span className="tenue">
                    Avisó {a.notified_by_name ?? 'la familia'} el{' '}
                    {fechaYHora(a.created_at)}
                  </span>
                  {a.session_date && (
                    <span className="tenue">
                      Clase del {fecha(a.session_date)}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>

          <nav className="paginacion">
            <button
              type="button"
              className="btn-plano"
              disabled={esLaPrimera(cursores) || avisos.isFetching}
              onClick={() => setCursores(anterior)}
            >
              Anterior
            </button>
            <span className="tenue">
              {esLaPrimera(cursores)
                ? `Primeros ${POR_PAGINA}`
                : `Página ${numeroDePagina(cursores)}`}
            </span>
            <button
              type="button"
              className="btn-plano"
              disabled={!avisos.data.has_more || avisos.isFetching}
              onClick={() =>
                setCursores((c) =>
                  siguiente(c, avisos.data?.next_cursor ?? null)
                )
              }
            >
              Siguiente
            </button>
          </nav>
        </>
      )}
    </section>
  );
}
